import {Form} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {GetCustomerPaymentApiResponse} from '@dms/api/finance';
import {GetCheckoutByIdApiResponse, Payment} from '@dms/api/sales';
import {useBank} from '@dms/shared';

import {suffixTestId} from 'shared/web';

import {useCheckoutPaymentIssuing} from '../hooks/useCheckoutPaymentIssuing';
import type {CheckoutPaymentCardProps} from '../types/CheckoutPaymentCardProps';
import {checkoutPaymentFormSchema, CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {buildPaymentFormDefaultValues} from '../utils/buildPaymentFormDefaultValues';
import {getIsFieldRequired} from '../utils/getIsFieldRequired';
import {CheckoutPaymentActions} from './CheckoutPaymentActions';
import {CheckoutPaymentFormBody} from './CheckoutPaymentFormBody';
import {CheckoutPaymentTemplateDropdown} from './CheckoutPaymentTemplateDropdown';
import {PaymentOverview} from './PaymentOverview';

interface CheckoutPaymentCardContentProps extends CheckoutPaymentCardProps {
  payment: Payment;
  checkout: GetCheckoutByIdApiResponse | undefined;
  customerPayment: GetCustomerPaymentApiResponse | undefined;
  hasIssueRestrictions?: boolean;
}

export function CheckoutPaymentCardContent(props: CheckoutPaymentCardContentProps) {
  const {getAftersalesDefaultBankAccount} = useBank();

  const paymentData = props.payment;
  const checkoutData = props.checkout;

  const uiCapabilities = paymentData.uiCapabilities;
  const allowedActions = uiCapabilities?.allowedActions ?? [];
  const isEditMode = uiCapabilities?.mode === 'EDIT';

  const {
    handleFormChange,
    issuePayment,
    resumeIssuing,
    isLazySaving,
    isIssuing,
    isResumingIssuing,
  } = useCheckoutPaymentIssuing({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
    payment: paymentData,
    checkout: checkoutData,
    serviceOrderId: props.serviceOrderId,
  });

  return isEditMode && isNotNil(uiCapabilities) ? (
    <Form<CheckoutPaymentFormType>
      mode="onSubmit"
      defaultValues={buildPaymentFormDefaultValues({
        payment: paymentData,
        customerPayment: props.customerPayment,
        defaultPaymentType: props.defaultPaymentType,
        defaultBankAccountValue:
          getAftersalesDefaultBankAccount(paymentData.foreignCurrencyPaymentAmount?.currency)
            ?.accountNumber ?? undefined,
      })}
      experimentalZodSchema={checkoutPaymentFormSchema(
        getIsFieldRequired(uiCapabilities.fieldState)
      )}
      onChange={handleFormChange}
      onSubmit={issuePayment}
      isDisabled={props.isEditingDisabled}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <CheckoutPaymentFormBody
            control={control}
            customerId={props.customerId}
            formApi={formApi}
            checkoutId={props.checkoutId}
            uiCapabilities={uiCapabilities}
            payment={paymentData}
            customerPayment={props.customerPayment}
            onSaveChanges={handleFormChange}
            totalAmount={checkoutData?.totalPrice?.withVat?.amount ?? 0}
            isEditingDisabled={props.isEditingDisabled}
            isForeignCurrencyAllowed={checkoutData?.isForeignCurrencyPayment}
            data-testid={suffixTestId('formBody', props)}
          />
          <CheckoutPaymentActions
            allowedActions={allowedActions}
            checkoutId={props.checkoutId}
            hasIssueRestrictions={props.hasIssueRestrictions}
            payment={paymentData}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
            templateDropdown={
              <CheckoutPaymentTemplateDropdown
                checkoutId={props.checkoutId}
                payment={paymentData}
                formApi={formApi}
                onTemplateSelect={() => handleFormChange(formApi.getValues())}
                data-testid={suffixTestId('template', props)}
              />
            }
            isSubmitButtonDisabled={
              isNilOrEmpty(checkoutData?.billingInformation?.customerInformation.id) ||
              isNilOrEmpty(checkoutData?.billingInformation?.contractInformation?.id)
            }
            onAddAnotherDepositClick={props.onAddAnotherDepositClick}
            onRemoveDepositClick={props.onRemoveDepositClick}
            isIssueLoading={isIssuing}
            isLazySaving={isLazySaving}
            onResumeIssuingClick={resumeIssuing}
            isResumeIssuingLoading={isResumingIssuing}
            control={control}
            contextTarget={props.contextTarget}
            contextId={props.contextId}
            actionContext={props.actionContext}
            serviceOrderId={props.serviceOrderId}
            data-testid={suffixTestId('actions', props)}
          />
        </VStack>
      )}
    </Form>
  ) : (
    <PaymentOverview
      paymentId={props.paymentId}
      checkoutId={props.checkoutId}
      customerId={props.customerId}
      serviceCaseId={props.serviceCaseId}
      serviceOrderId={props.serviceOrderId}
      payment={paymentData}
      isLazySaving={isLazySaving}
      onResumeIssuingClick={resumeIssuing}
      isResumeIssuingLoading={isResumingIssuing}
      isEditingDisabled={props.isEditingDisabled}
      isCompletingDisabled={props.isCompletingDisabled}
      onAddAnotherDepositClick={props.onAddAnotherDepositClick}
      onRemoveDepositClick={props.onRemoveDepositClick}
      isBalancePaymentIssued={props.isBalancePaymentIssued}
      contextTarget={props.contextTarget}
      contextId={props.contextId}
      actionContext={props.actionContext}
      data-testid={props['data-testid']}
    />
  );
}
